import React from 'react';
import {
  UtensilsCrossed, Coffee, Truck, Beer, Dices, Hotel,
  ClipboardList, AlertTriangle, MapPin, Quote,
  Paperclip, Camera, Mic, Smile, CheckCheck,
  History, FileText, Landmark, Monitor, MessageCircle, Wallet, Infinity as InfinityIcon,
} from 'lucide-react';
import KitLandingLayout from './KitLandingLayout';

const ACCENT_HEX = '#FF6600'; // GarzónBot orange — matches KitLandingLayout config
const LOGO_SRC = '/logo_garzonbot.png';

// ─────────────────────────────────────────────────────────────
// Section 0 (afterHero) — WhatsApp phone chat demo
// ─────────────────────────────────────────────────────────────
function PhoneChatSection() {
  return (
    <section className="py-14 px-6 bg-slate-950" data-testid="garzonbot-phone-demo">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
        {/* Copy */}
        <div className="text-center md:text-left">
          <div className="inline-flex items-center gap-3 mb-6">
            <img
              src={LOGO_SRC}
              alt="GarzónBot"
              className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
              data-testid="garzonbot-demo-badge-logo"
            />
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm sm:text-base font-bold tracking-wider uppercase"
              style={{ backgroundColor: 'rgba(255, 102, 0, 0.15)', color: ACCENT_HEX }}
            >
              Demo en vivo
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            Así conversa GarzónBot con tus clientes
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Todo por WhatsApp, sin descargar apps. El cliente pide, paga y GarzónBot avisa a la cocina en segundos.
          </p>
        </div>

        {/* Phone mockup */}
        <div className="flex justify-center md:justify-end">
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
}

function PhoneMockup() {
  return (
    <div
      className="relative w-[300px] sm:w-[340px] bg-black rounded-[3rem] p-2 shadow-2xl"
      style={{ boxShadow: '0 25px 60px -12px rgba(255, 102, 0, 0.25), 0 20px 40px -8px rgba(0,0,0,0.5)' }}
      data-testid="garzonbot-phone-mockup"
    >
      {/* Notch */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-20" />

      {/* Screen */}
      <div className="bg-[#ECE5DD] rounded-[2.5rem] overflow-hidden">
        {/* WhatsApp header */}
        <div className="bg-[#075E54] px-4 pt-8 pb-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 overflow-hidden">
            <img
              src={LOGO_SRC}
              alt="GarzónBot"
              className="w-9 h-9 object-contain"
              data-testid="garzonbot-whatsapp-header-logo"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-sm truncate">GarzónBot</div>
            <div className="text-white/70 text-xs">en línea</div>
          </div>
        </div>

        {/* Chat area */}
        <div
          className="px-3 py-4 space-y-2 min-h-[440px]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%23d9d0c1' fill-opacity='0.35'%3E%3Ccircle cx='10' cy='10' r='1'/%3E%3Ccircle cx='30' cy='20' r='1'/%3E%3Ccircle cx='50' cy='10' r='1'/%3E%3Ccircle cx='20' cy='40' r='1'/%3E%3Ccircle cx='40' cy='45' r='1'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        >
          {/* Bot bubble 1 */}
          <BotBubble time="14:02">Hola, soy GarzónBot. ¿En qué te puedo ayudar?</BotBubble>

          {/* User bubble with table tag */}
          <UserBubble tableTag="Mesa 33" time="14:04">
            Quiero por favor dos Pisco Sour y una Tabla de quesos.
          </UserBubble>

          {/* Bot bubble 2 with pay link */}
          <BotBubble time="14:04">
            ¡Perfecto! <span aria-hidden>🍹</span> dos Pisco Sour y una Tabla de quesos.
            <br />
            <br />
            Haz clic aquí para pagar y enviar tu pedido al bar y cocina:{' '}
            <a href="#" onClick={(e) => e.preventDefault()} className="text-[#075E54] font-semibold underline">
              Pagar Pedido
            </a>
          </BotBubble>
        </div>

        {/* Input bar */}
        <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2 border-t border-black/5">
          <div className="flex-1 bg-white rounded-full px-3 py-1.5 flex items-center gap-2 shadow-sm">
            <Smile className="w-4 h-4 text-gray-500 shrink-0" />
            <span className="text-xs text-gray-400 flex-1">Escribe un mensaje</span>
            <Paperclip className="w-4 h-4 text-gray-500 shrink-0" />
            <Camera className="w-4 h-4 text-gray-500 shrink-0" />
          </div>
          <div className="w-9 h-9 rounded-full bg-[#075E54] flex items-center justify-center shrink-0">
            <Mic className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BotBubble({ children, time }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] bg-white rounded-lg rounded-tl-none px-3 py-2 shadow-sm">
        <p className="text-sm text-gray-800 leading-snug">{children}</p>
        <div className="text-[10px] text-gray-500 text-right mt-1">{time}</div>
      </div>
    </div>
  );
}

function UserBubble({ children, time, tableTag }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] bg-[#DCF8C6] rounded-lg rounded-tr-none px-3 py-2 shadow-sm">
        {tableTag && (
          <div
            className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded mb-1"
            style={{ backgroundColor: 'rgba(255, 102, 0, 0.15)', color: ACCENT_HEX }}
          >
            {tableTag}
          </div>
        )}
        <p className="text-sm text-gray-800 leading-snug">{children}</p>
        <div className="text-[10px] text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
          {time}
          <CheckCheck className="w-3 h-3 text-[#34B7F1]" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Section 1 — Business categories (compact 6-column grid with
// circular tinted icon bubbles, matching the reference layout)
// ─────────────────────────────────────────────────────────────
const BUSINESS_CATEGORIES = [
  { icon: UtensilsCrossed, name: 'Restaurantes',   body: 'Atención a la mesa sin garzón adicional.',    tint: '#FF6600' },
  { icon: Coffee,          name: 'Cafeterías',     body: 'Pedidos rápidos sin fila.',                    tint: '#F59E0B' },
  { icon: Truck,           name: 'Food Trucks',    body: 'Caja rápida y pedidos al paso.',               tint: '#EF4444' },
  { icon: Beer,            name: 'Bares & Pubs',   body: 'Cuentas abiertas y pedidos desde el asiento.', tint: '#EAB308' },
  { icon: Dices,           name: 'Casinos',        body: 'El jugador pide sin levantarse, la máquina no para.', tint: '#8B5CF6' },
  { icon: Hotel,           name: 'Hoteles',        body: 'Room service y restaurante integrados.',       tint: '#3B82F6' },
];

function BusinessCategoriesSection() {
  return (
    <section className="py-16 px-6 bg-slate-950" data-testid="garzonbot-business-categories">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-4"
            style={{ backgroundColor: 'rgba(255, 102, 0, 0.15)', color: ACCENT_HEX }}
          >
            Perfecto para tu tipo de negocio
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">
            Soluciones a la medida de tu modelo de negocio
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {BUSINESS_CATEGORIES.map(({ icon: Icon, name, body, tint }, i) => (
            <div
              key={i}
              className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col items-center text-center hover:-translate-y-0.5 hover:shadow-lg transition-all"
              data-testid={`garzonbot-category-${i}`}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: `${tint}22` }}
              >
                <Icon className="w-8 h-8" style={{ color: tint }} aria-hidden />
              </div>
              <h3 className="text-sm font-semibold text-white mb-1.5">{name}</h3>
              <p className="text-xs text-slate-400 leading-snug">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Section 2 — "GarzónBot te Recuerda"
// ─────────────────────────────────────────────────────────────
const MEMORY_FEATURES = [
  { icon: ClipboardList, title: 'Preferencias y platos favoritos',    body: 'Recuerda qué pide habitualmente cada cliente frecuente.' },
  { icon: AlertTriangle, title: 'Alertas de alergias automáticas',    body: 'Avisa a la cocina si el cliente tiene alguna restricción declarada.' },
  { icon: MapPin,        title: 'Mesa habitual reconocida al instante', body: 'Identifica al cliente y sugiere su mesa preferida cuando llega.' },
];

function MemoryFeatureSection() {
  return (
    <section className="py-16 px-6 bg-slate-950 border-t border-slate-900" data-testid="garzonbot-memory-section">
      <div className="max-w-5xl mx-auto">
        {/* Header: logo on the left, title/subtitle on the right */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6 mb-12 text-center sm:text-left">
          <img
            src={LOGO_SRC}
            alt="GarzónBot"
            className="w-20 h-20 sm:w-24 sm:h-24 object-contain shrink-0"
            data-testid="garzonbot-memory-logo"
          />
          <div className="flex-1">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3 flex items-center justify-center sm:justify-start gap-3 flex-wrap">
              <span
                className="inline-flex w-11 h-11 sm:w-12 sm:h-12 rounded-xl items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(255, 102, 0, 0.15)' }}
                data-testid="garzonbot-memory-title-icon"
              >
                <History className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: ACCENT_HEX }} aria-hidden />
              </span>
              <span>GarzónBot te Recuerda</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl">
              GarzónBot conoce y aprende de tus clientes frecuentes. Recuerda su
              mesa habitual, sus platos favoritos y sus alergias — para una
              atención realmente personalizada.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {MEMORY_FEATURES.map(({ icon: Icon, title, body }, i) => (
            <div
              key={i}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
              data-testid={`garzonbot-memory-item-${i}`}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(255, 102, 0, 0.15)' }}
              >
                <Icon className="w-5 h-5" style={{ color: ACCENT_HEX }} aria-hidden />
              </div>
              <h3 className="text-base font-semibold text-white mb-1.5">{title}</h3>
              <p className="text-sm text-slate-300 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Section 3 — Customer testimonials
// ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote: 'Desde que instalamos GarzónBot, los pedidos llegan sin errores y mis garzones se enfocan en la atención, no en anotar.',
    name: 'Carlos Muñoz',
    role: 'Dueño, Resto Bar El Rincón, Temuco',
  },
  {
    quote: 'Lo mejor es que no necesitamos ninguna app ni tablet. Todo funciona en el celular del cliente.',
    name: 'Ana Fuentes',
    role: 'Gerente, Cafetería Ana\u2019s, Santiago',
  },
  {
    quote: 'Realmente GarzónBot ha sido una ayuda tremenda para nuestro restaurant en horas de alta demanda, cuando los garzones no pueden atender de inmediato en esas horas, GarzónBot es el complemento ideal para ellos.',
    name: 'Pedro Soto',
    role: 'Jefe de Operaciones, Restaurante El Puerto, Temuco',
  },
];

function TestimonialsSection() {
  return (
    <section className="py-16 px-6 bg-white" data-testid="garzonbot-testimonials">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 text-center mb-3">
          Lo que dicen nuestros clientes
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
          Restaurantes, cafeterías y locales que ya usan GarzónBot en su operación diaria.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all flex flex-col"
              data-testid={`garzonbot-testimonial-${i}`}
            >
              <Quote className="w-7 h-7 mb-4 shrink-0" style={{ color: ACCENT_HEX }} aria-hidden />
              <blockquote className="text-slate-200 text-sm sm:text-base leading-relaxed italic mb-6 flex-grow">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-auto">
                <div className="text-white font-semibold text-sm">{t.name}</div>
                <div className="text-slate-400 text-xs mt-0.5">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Section 4 — Integrations with third-party platforms
// ─────────────────────────────────────────────────────────────
const INTEGRATIONS = [
  { icon: FileText,      name: 'SII Chile',      badge: 'DTE',     tag: 'Emisión legal de boletas y facturas electrónicas sin salir del bot.' },
  { icon: Landmark,      name: 'Fintoc',         badge: 'SYNC',    tag: 'Cruza los depósitos bancarios con tus pedidos, sin planillas.' },
  { icon: Monitor,       name: 'POS Fudo',       badge: 'API',     tag: 'Menú y stock siempre sincronizados en tiempo real.' },
  { icon: MessageCircle, name: 'WhatsApp Cloud', badge: 'ALERTAS', tag: 'Avisos automáticos al cliente y al equipo, directo por chat.' },
  { icon: Wallet,        name: 'Wallet Passes',  badge: 'LOYALTY', tag: 'Sellos de fidelización digitales en el celular del cliente.' },
  { icon: InfinityIcon,  name: 'Transbank & MP', badge: 'MATCH',   tag: 'Match automático entre cobros con tarjeta y ventas registradas.' },
];

function IntegrationsSection() {
  return (
    <section
      className="py-16 px-6"
      style={{ background: 'linear-gradient(180deg, #14092E 0%, #1B0F3B 100%)' }}
      data-testid="garzonbot-integrations"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div
            className="inline-block px-4 py-1.5 rounded-full"
            style={{
              backgroundColor: 'rgba(255, 102, 0, 0.12)',
              border: '1px solid rgba(255, 102, 0, 0.25)',
            }}
          >
            <span
              className="text-xs sm:text-sm font-bold tracking-wider uppercase"
              style={{
                backgroundImage: 'linear-gradient(90deg, #FFB47A 0%, #FF8A3D 50%, #FF6600 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Integrado con las principales plataformas del mercado
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {INTEGRATIONS.map(({ icon: Icon, name, badge, tag }, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 flex flex-col items-center text-center transition-all hover:-translate-y-0.5 hover:shadow-2xl shadow-md border border-slate-200"
              data-testid={`garzonbot-integration-${i}`}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(255, 102, 0, 0.12)' }}
              >
                <Icon className="w-7 h-7" style={{ color: ACCENT_HEX }} aria-hidden />
              </div>
              <div className="flex items-center justify-center gap-1.5 mb-2 flex-wrap">
                <span className="text-gray-950 font-semibold text-sm">{name}</span>
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                  style={{ color: ACCENT_HEX, backgroundColor: 'rgba(255, 102, 0, 0.12)' }}
                >
                  {badge}
                </span>
              </div>
              <p className="text-xs leading-snug text-gray-600">{tag}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Kit config
// ─────────────────────────────────────────────────────────────
const config = {
  slug: 'garzonbot',
  name: 'GarzónBot',
  breadcrumb: 'Kit GarzónBot',
  industry: 'restaurantes y locales de comida',
  seoTitle: 'GarzónBot — Pedidos por WhatsApp para Restaurantes',
  seoDescription:
    'Tus clientes escanean un QR, piden y pagan por WhatsApp. Sin apps ni comisiones. El kit para restaurantes de ACBotHub.',
  accent: 'orange',
  accentHex: ACCENT_HEX,
  heroLogo: LOGO_SRC,
  titleIcon: LOGO_SRC,
  title: 'GarzónBot — tu restaurante toma pedidos solo',
  tagline:
    'Tus clientes escanean un QR en la mesa, eligen del menú y pagan por WhatsApp. Sin app, sin esperar al garzón, sin errores. Tu cocina recibe todo en tiempo real y el cliente sabe en todo momento dónde está su pedido.',
  stats: [
    { value: '0', label: 'apps que descargar' },
    { value: '1', label: 'escaneo de QR' },
    { value: '24/7', label: 'siempre activo' },
  ],
  features: [
    { title: 'QR por mesa', body: 'Genera un QR único por mesa y descarga el ZIP listo para imprimir.' },
    { title: 'Menú editable', body: 'Agrega, edita o desactiva platos en segundos desde el dashboard.' },
    { title: 'Carrito por WhatsApp', body: 'El cliente arma y confirma su pedido dentro del chat de WhatsApp.' },
    { title: 'Pago integrado', body: 'Flow, Klap o PayPal — todo directo desde WhatsApp, sin salir de la conversación.' },
    { title: 'Notificación a cocina', body: 'Cada pedido llega al instante con mesa, platos, cantidades y estado de pago.' },
    { title: 'Seguimiento en tiempo real', highlight: true, body: 'El cliente recibe mensaje en cada etapa: ✅ Confirmado → 🔥 En preparación → 🛵 En camino a tu mesa.' },
  ],
  flowSubtitle: 'De la mesa a la cocina en 5 pasos, sin llamar al garzón.',
  flow: [
    { title: 'Escanea el QR de su mesa', body: 'WhatsApp se abre automáticamente con el bot listo para atenderlo.' },
    { title: 'Elige del menú interactivo', body: 'Agrega platos al carrito con un simple mensaje.' },
    { title: 'Confirma y paga', body: 'Flow, Klap o PayPal — el cliente decide si paga ahora o al final.' },
    { title: 'Cocina recibe la notificación inmediata', body: 'Mesa, platos, cantidades y estado de pago llegan al instante al dashboard.' },
    { title: 'El cliente sabe dónde está su pedido', highlight: true, body: 'Pills de estado en tiempo real: ✅ Confirmado → 🔥 En preparación → 🛵 En camino a tu mesa.' },
  ],
  mockup: {
    left: {
      title: 'Pedidos del día',
      subtitle: 'Actualizado en vivo',
      rows: [
        { primary: 'Mesa 4 — 2× Pizza margarita, 1× Coca', secondary: 'Total $18.900 · hace 2 min', status: 'Pagado' },
        { primary: 'Mesa 7 — 1× Hamburguesa BBQ, 1× Papas', secondary: 'Total $12.400 · hace 5 min', status: 'En preparación' },
        { primary: 'Mesa 2 — 3× Empanadas de queso', secondary: 'Total $6.900 · hace 6 min', status: 'Confirmado' },
        { primary: 'Mesa 9 — 1× Sushi 12 piezas', secondary: 'Total $14.500 · hace 12 min', status: 'Despachado' },
      ],
    },
    right: {
      title: 'QR de mesa (preview)',
      action: 'Descargar todos en ZIP',
      rows: [
        { primary: 'Mesa 4', secondary: 'Escanea para pedir · restauranteX.acbothub.com/m/4', badge: 'Impreso' },
        { primary: 'Mesa 5', secondary: 'restauranteX.acbothub.com/m/5', badge: 'Pendiente' },
        { primary: 'Mesa 6', secondary: 'restauranteX.acbothub.com/m/6', badge: 'Pendiente' },
      ],
    },
  },
  // Intentionally empty — the "Perfecto para" section is replaced by the new
  // business categories section below (BusinessCategoriesSection).
  useCases: [],

  // Slots consumed by KitLandingLayout:
  afterHero: <PhoneChatSection />,
  beforeCta: (
    <>
      <BusinessCategoriesSection />
      <IntegrationsSection />
      <MemoryFeatureSection />
      <TestimonialsSection />
    </>
  ),
};

export default function GarzonBotKitPage() {
  return <KitLandingLayout config={config} />;
}
